/**
 * Pure guards for bulk-move drop / paste targets (Drive/Explorer parity).
 *
 * **This module is id math now, and the path math is gone** (STD-029 B-4). It
 * used to strip slashes, compute parent directories and ask whether one display
 * path was a prefix of another, because the move API was path-addressed and the
 * listing was a flat recursive projection in which a folder's whole subtree was
 * on screen. Neither is true: moves take a `parent_id` (B-3) and a listing is one
 * level (B-4).
 *
 * ### Cycles are structurally unreachable, which is why there is no cycle check
 *
 * A listing holds a node's direct children and its ancestor chain, and those are
 * the only drop targets. A dragged item's own descendants are therefore never on
 * screen, so "drop a folder into its own subtree" cannot be expressed:
 *
 *  - a **sibling** folder is not a descendant of the dragged folder — they share
 *    a parent;
 *  - an **ancestor** crumb is above the dragged folder, so moving there shortens
 *    the chain rather than closing it.
 *
 * The old `isDescendantOrSelf` walk existed to catch exactly the case the flat
 * projection made reachable. Deleting it is not a loosening: `check_file_object_placement`
 * walks ancestors under a per-org advisory lock and refuses a cycle for every
 * caller including the service role, and that is where the rule belongs (M-6).
 */
import type { FileItem, FolderRef } from './types';

/**
 * May the dragged/cut selection be moved into `target`?
 *
 * Blocks two things, and only two:
 *
 *  - **dropping onto one of the dragged items themselves.** dnd-kit will happily
 *    report a folder as the drop target while it is also being dragged.
 *  - **a pure no-op** — the target is the folder the items already live in.
 *    Allowing it would fire N PATCHes that change nothing and answer 200, which
 *    reads to a user as a move that silently did not happen.
 *
 * `currentParent` is where the listing is standing, which is where every visible
 * item lives — a one-level listing has no other possibility, and that is what
 * replaced the per-item `parentDir(item.key)` this used to compute.
 *
 * A `null` id on either side means the caller's own root. The two are compared
 * as-is: before the first listing answers, `currentParent` is `{id: null}` and no
 * crumb exists to drop onto, so the ambiguous pairing is unreachable.
 */
export function canMoveInto(
  items: readonly FileItem[],
  target: FolderRef,
  currentParent: FolderRef
): boolean {
  if (items.length === 0) return false;
  if (target.id !== null && items.some((item) => item.object_id === target.id)) {
    return false;
  }
  if (target.id === currentParent.id) return false;
  return true;
}
